import { useState } from 'react';
import { UserPlus, Mail } from 'lucide-react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@design-system';
import {
  AddPartnerDialog,
  InvitePartnerDialog,
  LinkExistingPartnerDialog,
} from '@/features/add-partner';

import { useSelector } from 'react-redux';
import { isNullOrUndefined, SessionType } from '@shared';
import {
  selectClient,
  selectClients,
  selectMaybeClientPartner,
  selectSession,
} from '@client/model/selectors';

type SendPartnerLinkRequestModalProps = {
  open: boolean;
  onClose: () => void;
};

type ClientPartnerAction = 'choose' | 'addPartner' | 'invitePartner';

export function SendPartnerLinkRequestModal({ open, onClose }: SendPartnerLinkRequestModalProps) {
  const client = useSelector(selectClient);
  const { clientId, clientFullName } = client;

  const clients = useSelector(selectClients);
  const clientPartner = useSelector(selectMaybeClientPartner);

  const { sessionType } = useSelector(selectSession);
  const [clientAction, setClientAction] = useState<ClientPartnerAction>('choose');

  function handleClose() {
    setClientAction('choose');
    onClose();
  }

  if (sessionType === SessionType.advisor || sessionType === SessionType.wizard) {
    if (clients.length === 0) {
      return (
        <Dialog
          open={open}
          onOpenChange={isOpen => {
            if (!isOpen) handleClose();
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Link Partner</DialogTitle>
              <DialogDescription />
            </DialogHeader>
            <p className="text-foreground py-4 text-base">
              As an advisor, you can create or edit partner links directly from your client's
              dashboard. No request or approval needed.
            </p>
          </DialogContent>
        </Dialog>
      );
    }

    return (
      <LinkExistingPartnerDialog
        open={open}
        client={client}
        onClose={handleClose}
      />
    );
  }

  if (!isNullOrUndefined(clientPartner)) {
    return (
      <Dialog
        open={open}
        onOpenChange={isOpen => {
          if (!isOpen) handleClose();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Partner</DialogTitle>
            <DialogDescription />
          </DialogHeader>
          <p className="text-foreground py-4 text-base">
            {clientFullName} is currently linked with {clientPartner.clientFullName}. To update
            this, please reach out to your advisor.
          </p>
        </DialogContent>
      </Dialog>
    );
  }

  if (clientAction === 'addPartner') {
    return (
      <AddPartnerDialog
        open={open}
        onOpenChange={isOpen => {
          if (!isOpen) handleClose();
        }}
        clientId={clientId}
        onSubmitSuccess={handleClose}
      />
    );
  }

  if (clientAction === 'invitePartner') {
    return (
      <InvitePartnerDialog
        open={open}
        onOpenChange={isOpen => {
          if (!isOpen) handleClose();
        }}
        onSuccess={handleClose}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Partner</DialogTitle>
          <DialogDescription>
            Choose how you&apos;d like to add your partner to your plan.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-4">
          <Button
            variant="outline"
            className="h-auto justify-start gap-3 p-4"
            onClick={() => setClientAction('addPartner')}
          >
            <UserPlus className="size-5 shrink-0" />
            <div className="text-left">
              <div className="font-medium">Add Partner</div>
              <div className="text-muted-foreground text-sm font-normal">
                Enter your partner&apos;s details to include them in your plan
              </div>
            </div>
          </Button>
          <Button
            variant="outline"
            className="h-auto justify-start gap-3 p-4"
            onClick={() => setClientAction('invitePartner')}
          >
            <Mail className="size-5 shrink-0" />
            <div className="text-left">
              <div className="font-medium">Invite Partner</div>
              <div className="text-muted-foreground text-sm font-normal">
                Send an email invitation to link with your partner&apos;s existing account
              </div>
            </div>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
